import { Button, Form, Input, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customerService } from "@/api/customerService";
import { messageService } from "@/api/messageService";
import { ZhTabs } from "@/components/common";
import { useUserSession } from "@/stores/session";
import { SafeCaptchaInput, SafeSettingFormShell } from "./SafeSettingFormParts";
import { maskSafeMobile } from "./UpdatePasswordPage";

interface CaptchaForm { captcha: string }
interface EmailForm { email: string; code: string }

/** 邮箱更新页：先验证当前身份，再验证并提交新的安全邮箱。 */
export function UpdateEmailPage() {
    const mobile = useUserSession(state => state.mobile);
    const currentEmail = useUserSession(state => state.email);
    const [stage, setStage] = useState<"verify" | "reset">("verify");
    const [verifyType, setVerifyType] = useState(1);
    const [identityCountdown, setIdentityCountdown] = useState(0);
    const [emailCountdown, setEmailCountdown] = useState(0);
    const [captchaForm] = Form.useForm<CaptchaForm>();
    const [emailForm] = Form.useForm<EmailForm>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const timers = useRef<ReturnType<typeof setInterval>[]>([]);
    const identityAccount = currentEmail && verifyType === 2 ? currentEmail : mobile;
    useEffect(() => () => timers.current.forEach(clearInterval), []);
    // 验证方式变化后清空旧验证码，避免手机号验证码被误用于邮箱验证。
    useEffect(() => { captchaForm.resetFields(["captcha"]); }, [captchaForm, verifyType]);

    /** 创建独立倒计时，身份验证码和新邮箱验证码互不干扰。 */
    const startCountdown = (setter: (updater: (value: number) => number) => void) => {
        setter(() => 60);
        const timer = setInterval(() => setter(value => {
            if (value <= 1) { clearInterval(timer); return 0; }
            return value - 1;
        }), 1000);
        timers.current.push(timer);
    };

    /** 向当前手机号或邮箱发送身份验证码。 */
    const sendIdentityCaptcha = async () => {
        if (identityCountdown) return;
        try {
            await messageService.sendCaptchaOnUpdatePassword(identityAccount);
            startCountdown(setIdentityCountdown);
        } catch { void message.error("验证码发送失败，请稍后重试"); }
    };
    /** 当前身份验证成功后进入新邮箱表单。 */
    const verifyIdentity = async () => {
        const { captcha } = await captchaForm.validateFields();
        try {
            const valid = await messageService.checkCaptchaOnSafeSetting(identityAccount, captcha);
            if (!valid) { void message.error("验证码错误"); return; }
            setStage("reset");
        } catch { void message.error("身份验证失败，请稍后重试"); }
    };
    /** 旧重置邮箱表单点击即启动倒计时并发送，格式校验只在最终提交时执行。 */
    const sendEmailCaptcha = async () => {
        startCountdown(setEmailCountdown);
        try {
            await messageService.sendCaptchaOnUpdatePassword(emailForm.getFieldValue("email"));
        } catch { void message.error("验证码发送失败，请稍后重试"); }
    };
    /** 后端同时校验邮箱验证码；成功后同步会话并返回安全设置。 */
    const submitEmail = async () => {
        const values = await emailForm.validateFields();
        try {
            const result = await customerService.updateEmail(values);
            if (!result) { void message.error("验证码错误"); return; }
            useUserSession.setState({ email: values.email });
            void message.success(`邮箱${searchParams.get("type") === "bind" ? "绑定" : "修改"}成功`);
            navigate("/me/safeSetting");
        } catch { void message.error("邮箱更新失败，请稍后重试"); }
    };

    return (
        <SafeSettingFormShell>
            {stage === "verify" ? (
                <div className="update-password-form">
                    <div className="update-password-title">
                        验证身份
                        <div className="update-password-tips">为确保是您本人操作，请先验证身份</div>
                    </div>
                    {currentEmail ? (
                        <div className="update-password-tabs">
                            <ZhTabs
                                active={verifyType}
                                options={[{ label: "手机号验证", value: 1 }, { label: "邮箱验证", value: 2 }]}
                                onChange={setVerifyType}
                            />
                        </div>
                    ) : null}
                    <Form
                        className={currentEmail ? "verify-form has-email" : "verify-form"}
                        colon={false}
                        form={captchaForm}
                        labelAlign="right"
                    >
                        <Form.Item label={currentEmail && verifyType === 2 ? "邮箱" : "手机号"}>
                            <span>{currentEmail && verifyType === 2 ? currentEmail : maskSafeMobile(mobile)}</span>
                        </Form.Item>
                        <Form.Item name="captcha" label="验证码" rules={[{ required: true, message: "请输入验证码" }]}>
                            <SafeCaptchaInput countdown={identityCountdown} onSend={() => void sendIdentityCaptcha()} />
                        </Form.Item>
                    </Form>
                    <div className="update-password-submit">
                        <Button type="primary" onClick={() => void verifyIdentity()}>立即验证</Button>
                    </div>
                </div>
            ) : (
                <div className="update-password-form reset-form">
                    <div className="update-password-title">
                        {searchParams.get("type") === "bind" ? "绑定" : "修改"}安全邮箱
                    </div>
                    <Form
                        className="reset-email-fields"
                        colon={false}
                        form={emailForm}
                        labelAlign="right"
                    >
                        <Form.Item
                            name="email"
                            label="邮箱地址"
                            rules={[
                                { required: true, message: "请输入邮箱地址" },
                                { pattern: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, message: "请输入正确的邮箱地址" }
                            ]}
                        >
                            <Input placeholder="请输入邮箱地址" />
                        </Form.Item>
                        <Form.Item name="code" label="验证码" rules={[{ required: true, message: "请输入验证码" }]}>
                            <SafeCaptchaInput
                                countdown={emailCountdown}
                                placeholder="请输入验证码"
                                onSend={() => void sendEmailCaptcha()}
                            />
                        </Form.Item>
                    </Form>
                    <div className="update-password-submit">
                        <Button type="primary" onClick={() => void submitEmail()}>确定</Button>
                    </div>
                </div>
            )}
        </SafeSettingFormShell>
    );
}
